import { z } from 'zod';
import { FlowApiPageLimitSchema, FlowApiPageOffsetSchema } from '../pagination/pageLimit.js';
/**
 * One row of a reference table, as a map from column name to cell.
 *
 * The columns are the table's own, so there is no shape to state beyond the types a cell may hold.
 */
export const FlowReferenceTableRowDataSchema = z.record(z.string(), z.union([z.string(), z.boolean(), z.number(), z.null()]));
/** Query parameters `GET /reference-tables` accepts. */
export const FlowReferenceTableListQuerySchema = z.object({
    q: z.string().describe('Return only tables whose name matches this.').optional(),
    organizationId: z
        .union([z.array(z.uuid()), z.uuid()])
        .describe('Return only tables of this organization, or of any of these organizations.')
        .optional(),
    limit: FlowApiPageLimitSchema.describe('How many tables to return.').optional(),
    offset: FlowApiPageOffsetSchema.describe('How many matches to skip, for paging.').optional(),
    includeDeleted: z.coerce
        .boolean()
        .describe('Include tables that have been deleted.')
        .optional()
        .default(false),
});
/** Path parameters the endpoints addressing one reference table accept. */
export const FlowReferenceTableIdParamsSchema = z.object({
    referenceTableId: z.uuid().describe('The reference table.'),
});
/**
 * Body `POST /reference-tables` accepts. Structural validation only: flow-api additionally checks
 * that the name is free within the organization, which needs the database.
 *
 * The rows arrive with the table itself. A reference table is read by flows as a whole, so there is
 * no endpoint that appends to one.
 */
export const FlowReferenceTableCreateBodySchema = z.object({
    name: z.string(),
    description: z.string(),
    organizationId: z.uuid().describe('Organization the table belongs to.'),
    data: z.array(FlowReferenceTableRowDataSchema).describe('The rows, in order.'),
});
/**
 * Body `PATCH /reference-tables/{id}` accepts. Sending `data` replaces every row.
 *
 * `deleted` may only be set to null, which restores a deleted table; deleting one goes through
 * `DELETE` rather than a patch.
 */
export const FlowReferenceTableUpdateBodySchema = z.object({
    name: z.string().optional(),
    description: z.string().optional(),
    data: z.array(FlowReferenceTableRowDataSchema).describe('Replacement rows, in order.').optional(),
    deleted: z.null().describe('Set to null to restore a deleted table.').optional(),
});
//# sourceMappingURL=referenceTable.js.map